import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH15_QUESTIONS } from "../data/neetPhysicsCh15Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 15: Waves (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Wave Basics & Types", "Speed of Waves", "Superposition & Standing Waves", "Beats & the Doppler Effect"];

const SECTION_COLORS = {
  "Wave Basics & Types":               "#2563eb",
  "Speed of Waves":                    "#ea580c",
  "Superposition & Standing Waves":    "#7c3aed",
  "Beats & the Doppler Effect":        "#dc2626",
};

const CONCEPTS = [
  // ── Wave Basics & Types ──────────────────────────────────────────────────────
  { section: "Wave Basics & Types", title: "Transverse waves vs longitudinal waves",
    explanation: "In a transverse wave the particles of the medium oscillate PERPENDICULAR to the direction the wave travels -- e.g. a wave running along a stretched string, or light. In a longitudinal wave the particles oscillate PARALLEL to the direction of travel, alternately crowding together (compressions) and spreading apart (rarefactions) -- e.g. sound in air. Because transverse waves need the medium to resist shearing (a sideways push), they travel through solids (and along the surface of liquids) but not through the bulk of liquids or gases; longitudinal waves only need volume (bulk) elasticity, so they travel through solids, liquids AND gases.",
    example: `Transverse wave (e.g. string):        Longitudinal wave (e.g. sound):
   /\\      /\\      /\\               ...||..||..||..||...
  /  \\    /  \\    /  \\              compression   rarefaction
particle motion (up-down)          particle motion (back-forth)
wave motion --> (sideways)          wave motion --> (same line)` },
  { section: "Wave Basics & Types", title: "The traveling wave equation y(x,t) = A sin(kx − ωt)",
    explanation: "A sinusoidal wave moving in the +x direction with amplitude A, wave number k, and angular frequency ω is written y(x,t) = A sin(kx − ωt); a wave moving in the −x direction is written y(x,t) = A sin(kx + ωt). At any FIXED position x, y varies sinusoidally in time with angular frequency ω; at any FIXED instant t, y varies sinusoidally in space with wave number k. The wave's speed follows as v = ω/k.",
    example: `y(x,t) = 0.02 sin(2x - 100t)   (SI units, moving in +x direction)

Compare with y = A sin(kx - wt):
A = 0.02 m
k = 2 rad/m        w (omega) = 100 rad/s

lambda = 2*pi/k = 2*pi/2 = pi ~ 3.14 m
T = 2*pi/w = 2*pi/100 ~ 0.063 s      f = 1/T ~ 15.9 Hz
v = w/k = 100/2 = 50 m/s   (check: v = f*lambda = 15.9 x 3.14 ~ 50 m/s, OK)` },
  { section: "Wave Basics & Types", title: "Wavelength, frequency, time period and wave speed (v = fλ)",
    explanation: "Wavelength λ is the distance between two consecutive points in the same phase (e.g. crest to crest); frequency f is the number of oscillations per second; time period T = 1/f is the time for one complete oscillation. A wave's speed through a given medium is fixed by the medium's properties, and it links these quantities through v = fλ = λ/T. So if the source's frequency changes but the medium doesn't, the wavelength must change to keep v constant.",
    example: `A wave in air: f = 500 Hz, lambda = 0.68 m
v = f x lambda = 500 x 0.68 = 340 m/s   (matches typical speed of sound in air)

If f is doubled to 1000 Hz in the SAME medium (v unchanged):
lambda_new = v/f = 340/1000 = 0.34 m   (wavelength halves)` },
  { section: "Wave Basics & Types", title: "Wave number k = 2π/λ and angular frequency ω = 2π/T",
    explanation: "The wave number k = 2π/λ measures the phase change (in radians) per unit distance, just as the angular frequency ω = 2π/T = 2πf measures the phase change per unit time. Together they give the wave's speed as v = ω/k, an alternative route to the same v = fλ relation.",
    example: `lambda = 0.5 m,  T = 0.005 s (5 ms)

k = 2*pi/lambda = 2*pi/0.5 = 4*pi ~ 12.57 rad/m
w = 2*pi/T  = 2*pi/0.005 = 400*pi ~ 1256.6 rad/s

v = w/k = 1256.6/12.57 ~ 100 m/s
check: f = 1/T = 200 Hz,  v = f x lambda = 200 x 0.5 = 100 m/s   OK` },

  // ── Speed of Waves ────────────────────────────────────────────────────────────
  { section: "Speed of Waves", title: "Speed of a transverse wave on a stretched string (v = √(T/μ))",
    explanation: "A transverse wave travels along a stretched string at speed v = √(T/μ), where T is the tension in the string and μ = m/L is its linear mass density (mass per unit length). A tighter string (larger T) carries waves faster; a heavier string (larger μ) carries them slower. Notably, this speed depends only on the string's tension and mass distribution -- not on the wave's own frequency or amplitude.",
    example: `String: length L = 2 m, mass m = 10 g = 0.01 kg, tension T = 40 N

mu = m/L = 0.01/2 = 0.005 kg/m
v = sqrt(T/mu) = sqrt(40/0.005) = sqrt(8000) ~ 89.4 m/s` },
  { section: "Speed of Waves", title: "Linear mass density and its effect on wave speed",
    explanation: "Since v = √(T/μ), wave speed on a string is inversely proportional to the square root of its linear mass density: v ∝ 1/√μ. Comparing two strings under the SAME tension, the one with higher mass per unit length always carries transverse waves more slowly -- this is why a guitar's thick (bass) strings, tuned in the same tension range as the thin strings, vibrate more sluggishly and produce lower notes.",
    example: `Same tension T on two strings; mu2 = 4 x mu1

v1 = sqrt(T/mu1)
v2 = sqrt(T/mu2) = sqrt(T/(4*mu1)) = (1/2) x sqrt(T/mu1) = v1/2

So the 4x heavier string carries the wave at HALF the speed.` },
  { section: "Speed of Waves", title: "Speed of sound in a medium — dependence on elasticity and density",
    explanation: "The speed of a longitudinal wave (sound) through a medium is v = √(E/ρ), where E is the relevant elastic modulus of the medium (bulk modulus for fluids, Young's modulus for a solid rod) and ρ is its density. Even though solids are generally denser than liquids and gases, their elastic moduli are enormously larger, so the NET effect is that sound travels fastest in solids, slower in liquids, and slowest in gases.",
    example: `Approximate speed of sound (order of magnitude):
Steel (solid):   ~5000 m/s   (very high Young's modulus)
Water (liquid):  ~1480 m/s   (high bulk modulus, high density)
Air (gas):        ~340 m/s   (low bulk modulus)

v_solid > v_liquid > v_gas` },
  { section: "Speed of Waves", title: "Speed of sound in air and its temperature dependence",
    explanation: "For an ideal gas, v = √(γRT/M), so the speed of sound in air increases with absolute temperature (v ∝ √T). Near room temperature this is well approximated by the linear rule v_t ≈ v0 + 0.6t, where v0 ≈ 331 m/s is the speed at 0°C and t is the temperature in °C. Humidity and pressure have only a small additional effect.",
    example: `v0 = 331 m/s at 0 C

At t = 25 C:
v ~ v0 + 0.6*t = 331 + 0.6 x 25 = 331 + 15 = 346 m/s

(commonly rounded to ~340-346 m/s for sound in air at room temperature)` },

  // ── Superposition & Standing Waves ─────────────────────────────────────────────
  { section: "Superposition & Standing Waves", title: "Principle of superposition of waves",
    explanation: "When two or more waves overlap at the same point in a linear medium, the resultant displacement is simply the algebraic (or vector) SUM of the displacements each wave would have produced alone. This holds regardless of how many waves overlap, and is the basis for explaining interference, standing waves, and beats.",
    example: `y_total(x,t) = y1(x,t) + y2(x,t) + ...

Two waves meet in phase, amplitudes 3 and 4 units:
  resultant amplitude = 3 + 4 = 7 units      (constructive)

Same two waves meet exactly out of phase (180 degrees):
  resultant amplitude = |4 - 3| = 1 unit     (destructive)` },
  { section: "Superposition & Standing Waves", title: "Standing waves — formation, nodes and antinodes",
    explanation: "A standing (stationary) wave forms when two waves of equal amplitude and frequency travel through the same region in OPPOSITE directions (typically an incident wave and its reflection). Unlike a traveling wave, its pattern does not move along the medium: certain points called NODES always have zero displacement, while points called ANTINODES oscillate with maximum amplitude. Adjacent nodes (and adjacent antinodes) are separated by λ/2; a node and the next antinode are separated by λ/4.",
    example: `y1 = A sin(kx - wt)      y2 = A sin(kx + wt)     (opposite directions)

y = y1 + y2 = 2A sin(kx) cos(wt)      <- standing wave

Nodes:     sin(kx) = 0   ->  x = 0, lambda/2, lambda, 3*lambda/2, ...
Antinodes: sin(kx) = +-1 ->  x = lambda/4, 3*lambda/4, 5*lambda/4, ...` },
  { section: "Superposition & Standing Waves", title: "Standing waves on a string fixed at both ends — allowed harmonics",
    explanation: "A string of length L fixed at BOTH ends must have a node at each end (zero displacement there at all times). This boundary condition allows only wavelengths λn = 2L/n (n = 1, 2, 3, ...), giving allowed frequencies fn = n·v/(2L), where v is the wave speed on the string. Only this discrete set of standing-wave patterns ('modes') can exist on the string.",
    example: `L = 1.5 m,  v = 300 m/s (speed of the wave ON the string)

f1 = v/(2L) = 300/(2 x 1.5) = 300/3 = 100 Hz     (fundamental, n=1)
f2 = 2 x f1 = 200 Hz                              (n=2)
f3 = 3 x f1 = 300 Hz                              (n=3)` },
  { section: "Superposition & Standing Waves", title: "Fundamental frequency, harmonics and overtones",
    explanation: "The lowest allowed frequency f1 = v/(2L) is called the FUNDAMENTAL (or first harmonic); it has 1 antinode and 2 nodes (just the fixed ends). Every higher allowed frequency fn = n·f1 is called the nth harmonic, or the (n−1)th OVERTONE; the nth harmonic has n antinodes and (n+1) nodes, since one more full loop is fitted onto the string. Since v = √(T/μ), the fundamental frequency also equals f1 = (1/2L)√(T/μ).",
    example: `L = 0.6 m,  T = 90 N,  mu = 0.01 kg/m

v = sqrt(T/mu) = sqrt(90/0.01) = sqrt(9000) ~ 94.9 m/s
f1 = v/(2L) = 94.9/1.2 ~ 79.1 Hz     (fundamental)

3rd harmonic (n=3): 3 antinodes, 4 nodes (incl. both ends)
f3 = 3 x 79.1 ~ 237.3 Hz` },

  // ── Beats & the Doppler Effect ──────────────────────────────────────────────────
  { section: "Beats & the Doppler Effect", title: "Beats and beat frequency",
    explanation: "When two sound sources of slightly different frequencies f1 and f2 are heard together, their superposition produces a periodic rise and fall in loudness called BEATS. The beat frequency -- the number of loudness maxima heard per second -- equals the difference of the two frequencies: f_beat = |f1 − f2|. Beats are noticeable only when f1 and f2 are close together (a few Hz apart); if they're far apart, the ear can no longer resolve the beating.",
    example: `Tuning fork A: f1 = 256 Hz
Tuning fork B: f2 = 260 Hz

f_beat = |f1 - f2| = |256 - 260| = 4 Hz   -> 4 beats heard per second` },
  { section: "Beats & the Doppler Effect", title: "Doppler effect — moving source",
    explanation: "When a sound source moves relative to a stationary observer, the observer hears a frequency f' different from the source's actual frequency f. If the source moves TOWARD the observer at speed vs (with v = speed of sound in the medium), successive wavefronts are crowded closer together and f' = f·v/(v − vs) (f' > f). If the source moves AWAY, wavefronts are stretched apart and f' = f·v/(v + vs) (f' < f).",
    example: `f = 600 Hz,  v (sound) = 340 m/s,  vs = 34 m/s

Source approaching: f' = f*v/(v-vs) = 600x340/(340-34) = 600x340/306 ~ 666.7 Hz
Source receding:     f' = f*v/(v+vs) = 600x340/(340+34) = 600x340/374 ~ 545.5 Hz` },
  { section: "Beats & the Doppler Effect", title: "Doppler effect — moving observer and sign conventions",
    explanation: "If instead the observer moves while the source is stationary, the observer's own motion changes how often wavefronts are encountered -- no compression of the wavefronts in the medium itself occurs. Moving TOWARD the source at speed v0: f' = f·(v + v0)/v (f' > f). Moving AWAY from the source: f' = f·(v − v0)/v (f' < f). Combining both effects when source and observer approach each other gives the general formula f' = f·(v + v0)/(v − vs); each sign flips if that party instead recedes.",
    example: `f = 500 Hz,  v (sound) = 340 m/s,  v0 = 17 m/s

Observer approaching source:  f' = f*(v+v0)/v = 500x357/340 = 525 Hz
Observer receding from source: f' = f*(v-v0)/v = 500x323/340 = 475 Hz` },
  { section: "Beats & the Doppler Effect", title: "Applications of the Doppler effect",
    explanation: "The Doppler effect explains the familiar drop in pitch as a fast-moving vehicle's horn or siren passes a stationary listener -- higher pitch on approach, lower pitch while receding. The same principle underlies RADAR speed guns (reflected radio waves are Doppler-shifted by a moving vehicle to measure its speed), SONAR, medical Doppler ultrasound (blood-flow measurement), and astronomy, where the red-shift or blue-shift of starlight reveals whether a star or galaxy is moving away from or toward Earth.",
    example: `Ambulance siren, f = 600 Hz, moving at vs = 20 m/s, v(sound) = 340 m/s

Approaching: f' = 600x340/(340-20) = 600x340/320 = 637.5 Hz    (higher pitch)
Receding:    f' = 600x340/(340+20) = 600x340/360 ~ 566.7 Hz    (lower pitch)

-> pitch audibly DROPS from ~637.5 Hz to ~566.7 Hz as it passes` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh15Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh15Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh15Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH15_QUESTIONS : NEET_PHYSICS_CH15_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 15</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Waves</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 15 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH15_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH15_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
